/* Free link removal tool with Marseille or Savannah.
   The gift is a separate $0.00 product (handle: free-link-removal-tool-gift),
   NOT the paid tool discounted to zero, so the line is always free. This script
   adds that gift product when a qualifying band is in the cart, caps it at one,
   and removes it again when the last qualifying band goes. The gift simply
   follows the band: there is no dismissal, and the customer gets no controls on
   the gift line - it cannot be removed on its own (the quantity stepper and the
   remove button are hidden on that line by CSS in layout/theme.liquid), and if
   it is removed by any other means while a qualifying band is still in the cart
   it comes straight back. GIFT_PRODUCT_ID / GIFT_VARIANT_ID must match the live
   Shopify gift product: if it is ever recreated, update both ids here. */
import Cart, { CartStore } from 'cart-store';

const GIFT_PRODUCT_ID = 15267504685360;           // Free Link Removal Tool (gift, $0.00)
const GIFT_VARIANT_ID = 67728433578288;           // its single variant
const TRIGGER_PRODUCT_IDS = [331417321512, 1490850545766]; // Marseille, Savannah
const PROPERTY_KEY = '_free_gift';
const VERIFY_DELAY = 600;    // ms; comfortably longer than a /cart.js round trip

let busy = false;
let selfRefresh = false;     // true while our own Cart.refresh() is in flight
let pending = null;          // latest cart event that arrived while busy
let verifyTimer = null;

const isGift = (i) => i.variant_id === GIFT_VARIANT_ID || i.product_id === GIFT_PRODUCT_ID;
const qualifies = (cart) => cart.items.some((i) => TRIGGER_PRODUCT_IDS.includes(i.product_id));
const anyGift = (cart) => cart.items.find(isGift);
const autoGift = (cart) => cart.items.find((i) => isGift(i) && i.properties && i.properties[PROPERTY_KEY]);

/* The drawer, its "(n items)" counter, the header badge and the cart page are
   all painted from cart-store subscriber notifications - there is no section
   re-fetch to re-trigger. The store writes the cart last-writer-wins
   (`const cart = await CartStore.getCart(); set(draft => draft.cart = cart)`)
   with no request sequencing, so a slower request already in flight when we
   add the gift can land afterwards and overwrite the store with a pre-gift
   snapshot. Cart.refresh() is the theme's own public repaint: it re-reads
   /cart.js and always writes a fresh object, so every subscriber - including
   components that subscribed part-way through an earlier notification - is
   notified again. */
async function repaint() {
  selfRefresh = true;
  try { await Cart.refresh(); } catch (e) { /* network hiccup */ }
  finally { selfRefresh = false; }
}

/* Second line of defence: if a straggler write clobbers the store after our
   repaint, the store's item_count stops matching the real cart. */
function verifyLater() {
  clearTimeout(verifyTimer);
  verifyTimer = setTimeout(async () => {
    try {
      const live = await CartStore.getCart();
      const shown = Cart.getState().cart;
      if (!shown || shown.item_count !== live.item_count) await repaint();
    } catch (e) { /* leave it alone */ }
  }, VERIFY_DELAY);
}

async function reconcile(cart, type) {
  if (!cart || !Array.isArray(cart.items)) return;
  // Our own refresh: never act on it, never loop.
  if (selfRefresh) return;
  if (busy) { pending = { cart, type }; return; }

  const gift = autoGift(cart);
  let changed = false;
  try {
    if (gift && gift.quantity > 1) {
      // One gift per cart, however the quantity got raised.
      busy = true; changed = true;
      await Cart.change({ id: gift.key, quantity: 1 });
    } else if (qualifies(cart) && !anyGift(cart)) {
      busy = true;
      // Re-check the live cart first: the event snapshot can be stale.
      const fresh = await CartStore.getCart();
      if (qualifies(fresh) && !anyGift(fresh)) {
        changed = true;
        await Cart.add({
          id: GIFT_VARIANT_ID,
          quantity: 1,
          'properties[_free_gift]': 'Free with Marseille or Savannah',
          'properties[Gift]': 'Free with your Marseille or Savannah band'
        });
      }
    } else if (!qualifies(cart) && gift) {
      busy = true; changed = true;
      await Cart.change({ id: gift.key, quantity: 0 });
    }
  } catch (e) {
    // Out of stock or a cart error: fail quietly, the customer can still check out.
  } finally {
    busy = false;
    if (changed) {
      await repaint();
      verifyLater();
    }
    if (pending) {
      const next = pending;
      pending = null;
      reconcile(next.cart, next.type);
    }
  }
}

document.addEventListener('on:cart:loaded', (event) => {
  const { cart, type } = event.detail || {};
  reconcile(cart, type);
});

// Carts that already exist when the page loads (e.g. built before this script existed).
CartStore.getCart().then((cart) => { reconcile(cart, 'INIT'); }).catch(() => {});
